import {
  Intent as BlueprintIntent,
  TextArea as BlueprintTextArea,
} from '@blueprintjs/core';
import uniq from 'lodash/uniq';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  ChipInput,
  Input,
  LoadingIcon,
  Modal,
  Selector,
  Textarea,
  Tooltip,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  EDIT_ORGANIZATION_DIALOG_CANCEL,
  EDIT_ORGANIZATION_DIALOG_DESCRIPTION_INPUT,
  EDIT_ORGANIZATION_DIALOG_NAME_INPUT,
  EDIT_ORGANIZATION_DIALOG_SELECTOR_CHIP_BUTTON,
  EDIT_ORGANIZATION_DIALOG_SELECTOR_CHIP_ITEM,
  EDIT_ORGANIZATION_DIALOG_SELECTOR_FILTER,
  EDIT_ORGANIZATION_DIALOG_SELECTOR_INPUT,
  EDIT_ORGANIZATION_DIALOG_SELECTOR_ITEM,
  EDIT_ORGANIZATION_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { runSequentially } from 'src/ui/containers/organization/hooks/helpers';
import {
  useInviteOrgMembers,
  useRemoveOrgInvites,
  useRemoveOrgMembers,
  useUpdateOrgMembersRole,
} from 'src/ui/containers/organization/hooks/invitations';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useEditOrganizationMutation } from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import {
  OrganizationRole,
  useInvalidateOrganization,
  useInvalidateOrganizationMembers,
  useOrganization,
} from 'src/ui/hooks/organization';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';
import { isValidEmail } from 'src/ui/utils/isValidEmail';

const isOpenSelector = isDialogOpenSelector(DialogType.EDIT_ORGANIZATION);

const getValuesFromQuery = (query: string) =>
  query
    .trim()
    .split(/[,\s]/)
    .map((value) => value.trim())
    .filter((value) => value && isValidEmail(value));

export const EditOrganizationDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();

  const { organization, membersByEmail, invitationsByEmail } = useOrganization(
    org
  );
  const invalidateOrg = useInvalidateOrganization(org);
  const invalidateMembers = useInvalidateOrganizationMembers(org);

  const initialAdmins = React.useMemo(() => {
    const emails: string[] = [];
    Object.values(membersByEmail).forEach((member) => {
      if (member.role === OrganizationRole.ADMIN) {
        emails.push(member.email);
      }
    });

    Object.values(invitationsByEmail).forEach((invitation) => {
      if (invitation.role === OrganizationRole.ADMIN) {
        emails.push(invitation.email);
      }
    });
    return emails;
  }, [membersByEmail, invitationsByEmail]);

  const memberItems = React.useMemo(
    () =>
      Object.values(membersByEmail).map<SelectItem<null>>((member) => ({
        value: member.email,
        text: `${member.firstName} ${member.lastName}`,
        subText: member.email,
      })),
    [membersByEmail]
  );

  const isOpen = useAppSelector(isOpenSelector);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const [name, setName] = React.useState(organization?.name || '');
  const [description, setDescription] = React.useState(
    organization?.description || ''
  );
  const ownerEmail = Object.values(membersByEmail).find(
    (member) => member.role === OrganizationRole.OWNER
  )?.email;
  const [admins, setAdmins] = React.useState<string[]>(initialAdmins);
  const [adminsQuery, setAdminsQuery] = React.useState<string>('');
  const domain = organization?.domain || '';

  const textAreaRef = React.useRef<BlueprintTextArea>(null);
  React.useEffect(() => {
    if (!description && textAreaRef.current?.textareaElement) {
      // reset text area height when description is cleared
      textAreaRef.current.setState({ height: undefined });
    }
  }, [description, textAreaRef]);

  React.useEffect(() => {
    if (isOpen && organization) {
      setName(organization.name);
      setDescription(organization.description || '');
      setAdmins(initialAdmins);
    }
  }, [initialAdmins, isOpen, organization, ownerEmail]);

  const {
    isLoading: isInviting,
    mutateAsync: inviteAsync,
  } = useInviteOrgMembers();
  const {
    isLoading: isRemovingInvites,
    mutateAsync: removeInvitesAsync,
  } = useRemoveOrgInvites();
  const {
    isLoading: isRemovingMembers,
    mutateAsync: removeMembersAsync,
  } = useRemoveOrgMembers();
  const {
    isLoading: isUpdatingMembersRole,
    mutateAsync: updateMembersRoleAsync,
  } = useUpdateOrgMembersRole();
  const {
    isLoading: isEditing,
    mutateAsync: editOrganizationAsync,
  } = useEditOrganizationMutation(graphQLClient, { onMutate, onError });

  const isLoading =
    isInviting ||
    isRemovingInvites ||
    isRemovingMembers ||
    isUpdatingMembersRole ||
    isEditing;

  const handleClose = () => {
    dispatch(closedDialog());
    setName('');
    setDescription('');
    setAdmins([]);
    setAdminsQuery('');
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();

    if (!trimmedName || isLoading) {
      return;
    }

    const inviteEmails: string[] = [];
    const updateMemberEmails: string[] = [];
    admins.forEach((email) => {
      if (!initialAdmins.includes(email)) {
        inviteEmails.push(email);
        return;
      }

      const invitation = invitationsByEmail[email];
      if (
        invitation &&
        invitation.role === OrganizationRole.ADMIN &&
        invitation.expired
      ) {
        inviteEmails.push(email);
        return;
      }

      updateMemberEmails.push(email);
    });

    const removedMemberEmails: string[] = [];
    const removedInviteEmailsById: Record<string, string> = {};
    initialAdmins.forEach((email) => {
      if (!admins.includes(email)) {
        if (membersByEmail[email]) {
          removedMemberEmails.push(email);
        } else if (invitationsByEmail[email]) {
          removedInviteEmailsById[invitationsByEmail[email].id] = email;
        }
      }
    });
    const removeInviteIds = Object.keys(removedInviteEmailsById);

    const success = await runSequentially([
      {
        enabled: true,
        run: () =>
          editOrganizationAsync({
            org_domain: org,
            input: {
              name: trimmedName,
              description: description.trim() || undefined,
            },
          })
            .then(() => true)
            .catch((err) => {
              const errorMsg =
                typeof err === 'string' ? err : (err as Error)?.message;
              dispatch(
                queueNotification(`Error Editing Organization: ${errorMsg}`)
              );
              return false;
            }),
      },
      {
        enabled: inviteEmails.length,
        run: () =>
          inviteAsync({
            input: {
              emails: inviteEmails,
              organization_domain: org,
              organization_role: OrganizationRole.ADMIN,
            },
          }),
      },
      {
        enabled: removeInviteIds.length,
        run: () =>
          removeInvitesAsync({ ids: removeInviteIds }, removedInviteEmailsById),
      },
      {
        enabled: removedMemberEmails.length,
        run: () =>
          removeMembersAsync({
            org_domain: org,
            emails: removedMemberEmails,
          }),
      },
      {
        enabled: updateMemberEmails.length,
        run: () =>
          updateMembersRoleAsync({
            org_domain: org,
            emails: updateMemberEmails,
            new_role: OrganizationRole.ADMIN,
          }),
      },
    ]);

    invalidateOrg();
    invalidateMembers();

    if (success) {
      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.organization.editDialog.notifications.success,
        })
      );
      handleClose();
    }
  };

  let validationError: string;
  if (!name.trim()) {
    validationError =
      copy.components.organization.dialog.inputs.name.validationRequired;
  } else {
    validationError = '';
  }

  const isSubmitDisabled = Boolean(validationError || isLoading);

  return (
    <Modal
      canOutsideClickClose={false}
      className="organization-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={EDIT_ORGANIZATION_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.organization.editDialog.buttons.cancel}
          </Button>
          <Tooltip
            description={validationError}
            disabled={isLoading || !isSubmitDisabled}
          >
            <Button
              disabled={isSubmitDisabled}
              leftIcon={isLoading ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={EDIT_ORGANIZATION_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {copy.components.organization.editDialog.buttons.submit}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.organization.editDialog.title}
    >
      <div className="organization-dialog-body">
        <div className="organization-dialog-container">
          <Input
            clearable={false}
            label={copy.components.organization.dialog.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            placeholder=""
            required
            size="md"
            testId={EDIT_ORGANIZATION_DIALOG_NAME_INPUT}
            value={name}
          />
          <Body className="organization-dialog-input-footer" size="sm">
            {copy.components.organization.dialog.inputs.name.footer}
          </Body>
        </div>

        <Textarea
          className="organization-dialog-textarea"
          growVertically
          label={copy.components.organization.dialog.inputs.description.label}
          onChange={(e) => setDescription(e.target.value)}
          placeholder=""
          rows={2}
          testId={EDIT_ORGANIZATION_DIALOG_DESCRIPTION_INPUT}
          textAreaRef={textAreaRef}
          value={description}
        />

        <ChipInput
          clearable={false}
          disabled
          label={copy.components.organization.dialog.inputs.owner.label}
          size="md"
          values={ownerEmail ? [ownerEmail] : []}
        />

        <Selector.Multi<null, SelectItem, SelectItem[]>
          chipInputProps={{
            chipButtonTestId: EDIT_ORGANIZATION_DIALOG_SELECTOR_CHIP_BUTTON,
            chipTestId: EDIT_ORGANIZATION_DIALOG_SELECTOR_CHIP_ITEM,
            className: 'organization-dialog-chip-selector',
            clearable: true,
            label: copy.components.organization.dialog.inputs.admin.label,
            size: 'md',
            testId: EDIT_ORGANIZATION_DIALOG_SELECTOR_INPUT,
            values: admins,
          }}
          creatable={Boolean(getValuesFromQuery(adminsQuery).length)}
          createNewItemFromQuery={(query) => {
            const values = getValuesFromQuery(query);
            return values.length ? { value: query, isNewlyCreated: true } : [];
          }}
          createNewItemTestId={EDIT_ORGANIZATION_DIALOG_SELECTOR_ITEM('')}
          createText={(query) => {
            const values = getValuesFromQuery(query);
            return copy.components.organization.dialog.inputs.email.create(
              values
            );
          }}
          filterable
          filterTestId={EDIT_ORGANIZATION_DIALOG_SELECTOR_FILTER}
          items={memberItems.filter((item) => item.value !== ownerEmail)}
          itemTestId={EDIT_ORGANIZATION_DIALOG_SELECTOR_ITEM}
          onQueryChange={setAdminsQuery}
          onSelect={(selected) => {
            const values: string[] = [];
            selected.forEach((item) => {
              if (item.isNewlyCreated) {
                values.push(...getValuesFromQuery(item.value));
              } else {
                values.push(item.value);
              }
            });
            setAdmins(uniq(values));
          }}
          popoverProps={{
            portalClassName: 'organization-dialog-menu-portal',
          }}
          selected={admins.map(
            (value) =>
              memberItems.find((item) => item.value === value) || {
                value,
                text: value,
              }
          )}
        />

        <Input
          className="organization-dialog-input-monospace"
          clearable={false}
          disabled
          label={copy.components.organization.dialog.inputs.domain.label}
          placeholder=""
          size="md"
          value={domain}
        />
      </div>
    </Modal>
  );
};
